// src/services/rideService.js

import { supabase } from "../supabaseClient";

const toNullIfEmpty = (v) => {
  if (v === undefined || v === null) return null;
  const s = String(v).trim();
  return s.length ? s : null;
};

const toIntOrNull = (v) => {
  if (v === undefined || v === null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : null;
};

const normalizePhone = (v) => String(v || "").replace(/\D/g, "");

export const rideService = {
  /* ----------------------------------------------------------
     CREATE (public — form)
  ---------------------------------------------------------- */
  async createRideRequest(payload) {
    const insertRow = {
      customer_name: String(payload.customer_name || "").trim(),
      phone: normalizePhone(payload.phone),
      whatsapp: toNullIfEmpty(payload.whatsapp ? normalizePhone(payload.whatsapp) : null),
      email: toNullIfEmpty(payload.email),

      pickup_location: toNullIfEmpty(payload.pickup_location),
      drop_location: toNullIfEmpty(payload.drop_location),
      travel_date: toNullIfEmpty(payload.travel_date),
      pickup_time: toNullIfEmpty(payload.pickup_time),
      return_date: toNullIfEmpty(payload.return_date),
      passengers: toIntOrNull(payload.passengers),

      vehicle_type: toNullIfEmpty(payload.vehicle_type),
      service_type: toNullIfEmpty(payload.service_type),

      requirements: toNullIfEmpty(payload.requirements),

      status: "pending",
    };

    if (!insertRow.customer_name) throw new Error("Customer name is required");
    if (!insertRow.phone || insertRow.phone.length < 10) {
      throw new Error("Enter a valid mobile number");
    }
    if (!insertRow.pickup_location) throw new Error("Pickup location is required");

    const { data, error } = await supabase
      .from("ride_bookings")
      .insert([insertRow])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  /* ----------------------------------------------------------
     ADMIN — LIST
  ---------------------------------------------------------- */
  async listRideBookings({ status, search, limit = 200 } = {}) {
    let query = supabase
      .from("ride_bookings")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(limit);

    if (status) query = query.eq("status", status);

    if (search && search.trim()) {
      const s = `%${search.trim()}%`;
      query = query.or(
        `customer_name.ilike.${s},phone.ilike.${s},pickup_location.ilike.${s},drop_location.ilike.${s}`
      );
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  /* ----------------------------------------------------------
     ADMIN — GET SINGLE
  ---------------------------------------------------------- */
  async getRideBooking(id) {
    const { data, error } = await supabase
      .from("ride_bookings")
      .select("*")
      .eq("id", id)
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  /* ----------------------------------------------------------
     ADMIN — UPDATE STATUS / NOTES
  ---------------------------------------------------------- */
  async updateRideBooking(id, { status, admin_notes, assigned_to } = {}) {
    const patch = {};
    if (status !== undefined) patch.status = status;
    if (admin_notes !== undefined) patch.admin_notes = admin_notes;
    if (assigned_to !== undefined) patch.assigned_to = assigned_to;

    if (status && ["confirmed", "cancelled", "completed"].includes(status)) {
      patch.handled_at = new Date().toISOString();
    }

    const { data, error } = await supabase
      .from("ride_bookings")
      .update(patch)
      .eq("id", id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  /* ----------------------------------------------------------
     ADMIN — DELETE
  ---------------------------------------------------------- */
  async deleteRideBooking(id) {
    const { error } = await supabase
      .from("ride_bookings")
      .delete()
      .eq("id", id);
    if (error) throw error;
  },

  /* ----------------------------------------------------------
     ADMIN — STATS
  ---------------------------------------------------------- */
  async getRideBookingStats() {
    const statuses = ["pending", "contacted", "confirmed", "cancelled", "completed"];

    const counts = await Promise.all(
      statuses.map(async (s) => {
        const { count, error } = await supabase
          .from("ride_bookings")
          .select("id", { count: "exact", head: true })
          .eq("status", s);
        if (error) throw error;
        return { status: s, count: count || 0 };
      })
    );

    const { count: total } = await supabase
      .from("ride_bookings")
      .select("id", { count: "exact", head: true });

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const { count: todayCount } = await supabase
      .from("ride_bookings")
      .select("id", { count: "exact", head: true })
      .gte("created_at", today.toISOString());

    return {
      total: total || 0,
      today: todayCount || 0,
      byStatus: counts.reduce((acc, { status, count }) => {
        acc[status] = count;
        return acc;
      }, {}),
    };
  },

  /* ----------------------------------------------------------
     ADMIN — NOTIFICATIONS
  ---------------------------------------------------------- */
  async listAdminNotifications({ unreadOnly = false, limit = 30 } = {}) {
    let query = supabase
      .from("admin_notifications")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(limit);

    if (unreadOnly) query = query.eq("is_read", false);

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  async getUnreadNotificationCount() {
    const { count, error } = await supabase
      .from("admin_notifications")
      .select("id", { count: "exact", head: true })
      .eq("is_read", false);
    if (error) throw error;
    return count || 0;
  },

  async markNotificationRead(id) {
    const { error } = await supabase
      .from("admin_notifications")
      .update({ is_read: true })
      .eq("id", id);
    if (error) throw error;
  },

  async markAllNotificationsRead() {
    const { error } = await supabase
      .from("admin_notifications")
      .update({ is_read: true })
      .eq("is_read", false);
    if (error) throw error;
  },
};